// SPDX-FileCopyrightText: 2016-2026 Lari Natri <lari.natri@iki.fi>
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Populate the Translator with "DYNAMIC.*" translation keys derived from the
 * loaded book metadata (InitialState.books).
 *
 * These keys are not shipped as JSON translation files; they are computed from
 * the metadata so both SSR and the client can render the same content.
 */

import type { InitialState, SongbookAltVersionLink, SongbookAudioLink, SongbookTag } from "./types";
import {
  getEnabledBooks,
  joinUrl,
  normalizeI18nRecordToMap,
  SONGBOOK_DATA_BASE_URL,
} from "./songbook";
import type { Translator } from "@unilarva/translator";

export function addDynamicTranslationKeys(t: Translator, state: InitialState): void {
  const enabled = getEnabledBooks(state.books);

  const sourceTitles = new Map<string, Map<string, string>>();
  const sources = (state.books as any).book_sources as
    { source_book_id: string; title_i18n: any }[] | undefined;
  if (Array.isArray(sources)) {
    for (const src of sources) {
      if (!src || !src.source_book_id) continue;
      const map = normalizeI18nRecordToMap(src.title_i18n, state.site.languages);
      sourceTitles.set(src.source_book_id, map);
    }
  }

  enabled.forEach((book, idx0) => {
    const id = String(idx0 + 1);

    // Book title/subtitle
    t.setTranslation(
      `DYNAMIC.book.${id}.title`,
      normalizeI18nRecordToMap(book.title_i18n, state.site.languages),
    );
    t.setTranslation(
      `DYNAMIC.book.${id}.subtitle`,
      normalizeI18nRecordToMap(book.subtitle_i18n ?? null, state.site.languages),
    );

    // Book description (SEO/share)
    t.setTranslation(
      `DYNAMIC.book.${id}.description`,
      normalizeI18nRecordToMap(book.description_i18n ?? null, state.site.languages),
    );

    const srcId = (book as any).source_book_id as string | undefined;
    if (srcId && sourceTitles.has(srcId)) {
      const existing = sourceTitles.get(srcId)!;
      t.setTranslation(`DYNAMIC.source.${srcId}.title`, existing);
      sourceTitles.delete(srcId);
    }

    // Audio link titles
    const audio = (book.audio_links ?? []).filter(
      (x): x is SongbookAudioLink => !!x && !!x.enabled,
    );
    audio.forEach((link, idx1) => {
      const n = String(idx1 + 1);
      t.setTranslation(
        `DYNAMIC.book.${id}.audio.title.${n}`,
        normalizeI18nRecordToMap(link.title_i18n, state.site.languages),
      );
    });

    // Extra printout titles
    const extra = (book.extra_printout_links ?? []).filter(
      (x): x is SongbookAltVersionLink => !!x && !!x.enabled,
    );
    extra.forEach((link, idx1) => {
      const n = String(idx1 + 1);
      t.setTranslation(
        `DYNAMIC.book.${id}.extraprintout.title.${n}`,
        normalizeI18nRecordToMap(link.title_i18n, state.site.languages),
      );
    });
  });

  // Tag icon + text keys (used on cards + side navigation)
  const iconBasePath = joinUrl(SONGBOOK_DATA_BASE_URL, state.books.icon_path ?? "");
  for (const tag of state.books.tags ?? []) {
    addOneTagKeys(t, state, tag, iconBasePath);
  }

  // Variant tag icons/text (from global variant definitions)
  const variants = (state.books as any).variant_types as
    Record<string, { tag_ids?: string[]; label_i18n?: any }> | undefined;
  const tagList = state.books.tags ?? [];
  if (variants && tagList.length > 0) {
    Object.entries(variants).forEach(([variantId, def]) => {
      // Variant label (if provided) -> dynamic key usable by UI.
      if (def?.label_i18n) {
        t.setTranslation(
          `DYNAMIC.variant.${variantId}.label`,
          normalizeI18nRecordToMap(def.label_i18n, state.site.languages),
        );
      }

      const tagIds = def?.tag_ids ?? [];
      for (const tagId of tagIds) {
        const tag = tagList.find(t => t?.id === tagId);
        if (!tag) continue;
        // Reuse existing tag dynamic keys; nothing extra needed.
        // The UI will reference DYNAMIC.tag.<id>.* directly.
      }
    });
  }
}

function addOneTagKeys(
  t: Translator,
  state: InitialState,
  tag: SongbookTag,
  iconPath: string,
): void {
  t.setTranslation(
    `DYNAMIC.tag.${tag.id}.text`,
    normalizeI18nRecordToMap(tag.text_i18n, state.site.languages),
  );

  // iconfile_i18n is "record of strings"; normalize helper expects I18nRecord-like.
  const iconMap = normalizeI18nRecordToMap(
    (tag.iconfile_i18n ?? null) as any,
    state.site.languages,
  );
  if (iconMap.size === 0) return;

  iconMap.forEach((filename, lang) => {
    iconMap.set(lang, joinUrl(iconPath, filename));
  });

  t.setTranslation(`DYNAMIC.tag.${tag.id}.iconfile`, iconMap);
}
